import type { ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { IconBooklet, IconWrenchFill } from "@synthesis/shared-icons";
import { RZDLogo } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";

import { AppHeaderUserBlock } from "../app-header/nav-menu";

const navItemClass = "flex h-8 items-center gap-2 rounded-[10px] py-2 pl-3 pr-4 transition-colors hover:bg-neutrals-95/80";

/**
 * Пункт навигации ЛК администратора: пилюля 32px, активный — brand-40 на neutrals-99.
 * Если `to` не задан — страницы ещё нет, пункт рендерится неинтерактивным.
 */
const AdminNavItem = ({ isActive, label, icon, to }: { isActive?: boolean; label: string; icon: ReactNode; to: string }) => (
  <Link
    to={to}
    className={cn(navItemClass, isActive && "bg-neutrals-99")}
    aria-current={isActive ? "page" : undefined}
    activeOptions={{ exact: true }}
  >
    <span className={cn("inline-flex size-5 shrink-0 items-center justify-center", isActive ? "text-brand-40" : "text-neutrals-60")}>{icon}</span>
    <span className={cn("subtitle-xs mt-0.5", isActive ? "text-brand-40" : "text-type-minor")}>{label}</span>
  </Link>
);

/** Навбар ЛК администратора услуг: лого + «Конструктор / Справочники» + пользователь и уведомления. */
export const AdminConsoleNavbar = () => {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const inConstructor = pathname.startsWith("/admin") && !pathname.startsWith("/admin/reference");
  const inReference = pathname.startsWith("/admin/reference");

  return (
    <header className="bg-neutrals-99 fixed left-0 right-0 top-0 z-10 w-full">
      <div className="relative flex h-[var(--app-header-height)] items-center justify-between gap-6 px-8 py-2">
        <div className="flex min-w-0 items-center gap-4">
          <Link to="/" aria-label="На главную" className="shrink-0">
            <RZDLogo className="w-[4.25rem]" />
          </Link>
          <div className="bg-neutrals-90 h-[30px] w-px shrink-0" aria-hidden />
          <div className="flex min-w-0 flex-col justify-between gap-0.5">
            <p className="text-brand-30 truncate text-[13px] font-bold leading-[1.2]">Портал получения технических условий</p>
            <p className="subtitle-xs text-type-minor truncate leading-[1.2]">ОАО «Российские железные дороги»</p>
          </div>
        </div>

        <nav className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" aria-label="Навигация ЛК администратора">
          <div className="flex items-center gap-1">
            <AdminNavItem isActive={inConstructor} label="Конструктор" icon={<IconWrenchFill className="size-5" aria-hidden />} to="/admin" />
            <AdminNavItem
              isActive={inReference}
              label="Справочники"
              icon={<IconBooklet className="size-5" aria-hidden />}
              to="/admin/reference/document-types"
            />
          </div>
        </nav>

        <AppHeaderUserBlock />
      </div>
    </header>
  );
};
